import {
  ChangeEventHandler,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState
} from 'react'
import {
  FaCalendarAlt,
  FaSearch,
  FaSlidersH,
  FaStar,
  FaTimes,
  FaWallet
} from 'react-icons/fa'
import { useHistory, useLocation } from 'react-router-dom'

import { useQuery } from '@tanstack/react-query'

import api from 'services/api'
import { trackEvent } from 'services/analytics'

import { ModelItineraries } from 'models/ModelItineraries'
import { getItineraryPublicPath } from 'utils/itineraryUrl'

import { formatInterest, TRAVEL_INTEREST_VALUES } from 'travelInterests'

import * as S from './style'

type SearchType = {
  city: string
  interests: Array<string>
  min_price: string
  max_price: string
}

type SortMode = 'relevance' | 'rating' | 'recent' | 'price'

const PAGE_SIZE = 8

const getQueryString = ({
  city,
  interests,
  min_price,
  max_price
}: SearchType) => {
  const query = new URLSearchParams()

  if (city) query.set('city', city)
  if (interests.length > 0) query.set('interests', interests.join(','))
  if (min_price) query.set('min_price', min_price)
  if (max_price) query.set('max_price', max_price)

  const queryString = query.toString()
  return queryString ? `?${queryString}` : ''
}

const averageRating = (itinerary: ModelItineraries) => {
  if (!itinerary.rates?.length) return 0

  return (
    itinerary.rates.reduce((total, item) => total + Number(item.rate || 0), 0) /
    itinerary.rates.length
  )
}

const formatCurrency = (value?: number) => {
  const amount = Number(value)
  if (!Number.isFinite(amount) || amount <= 0) return 'Não informado'

  return amount.toLocaleString('pt-BR', {
    style: 'currency',
    currency: 'BRL',
    maximumFractionDigits: 0
  })
}

export default function ListItinerary() {
  const savedTimer = useRef<NodeJS.Timeout | null>(null)
  const { search } = useLocation()
  const { push } = useHistory()

  const parameters = useMemo(() => {
    const query = new URLSearchParams(search)

    return {
      city: query.get('city') ?? '',
      interests: query.get('interests')?.split(',').filter(Boolean) ?? [],
      min_price: query.get('min_price') ?? '',
      max_price: query.get('max_price') ?? ''
    }
  }, [search])

  const [params, setParams] = useState<SearchType>(parameters)
  const [sortMode, setSortMode] = useState<SortMode>('relevance')
  const [visibleCount, setVisibleCount] = useState(PAGE_SIZE)
  const [isMobileFiltersOpen, setIsMobileFiltersOpen] = useState(false)

  useEffect(() => {
    setParams(parameters)
  }, [parameters])

  useEffect(() => {
    if (!isMobileFiltersOpen) return

    const previousOverflow = document.body.style.overflow
    document.body.style.overflow = 'hidden'

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setIsMobileFiltersOpen(false)
    }

    window.addEventListener('keydown', handleKeyDown)

    return () => {
      document.body.style.overflow = previousOverflow
      window.removeEventListener('keydown', handleKeyDown)
    }
  }, [isMobileFiltersOpen])

  useEffect(() => {
    savedTimer.current = setTimeout(() => {
      const nextUrl = `/roteiros${getQueryString(params)}`
      const currentUrl = `/roteiros${search}`
      if (nextUrl !== currentUrl) push(nextUrl)
    }, 450)

    return () => {
      savedTimer.current && clearTimeout(savedTimer.current)
    }
  }, [params, push, search])

  const { data: itineraries = [], isLoading } = useQuery({
    queryKey: [
      'itineraries',
      parameters.city,
      parameters.interests.join(','),
      parameters.min_price,
      parameters.max_price
    ],
    queryFn: async () => {
      const { data }: { data: Array<ModelItineraries> } = await api.get(
        `/itineraries${getQueryString(parameters)}`
      )
      return data ?? []
    },
    staleTime: 5 * 60 * 1000
  })

  const availableInterests = TRAVEL_INTEREST_VALUES

  const sortedItineraries = useMemo(() => {
    const result = [...itineraries]

    if (sortMode === 'rating') {
      return result.sort((a, b) => averageRating(b) - averageRating(a))
    }

    if (sortMode === 'recent') {
      return result.sort(
        (a, b) =>
          new Date(b.createdAt || 0).getTime() -
          new Date(a.createdAt || 0).getTime()
      )
    }

    if (sortMode === 'price') {
      return result.sort((a, b) => {
        const aPrice = Number(a.spent) > 0 ? Number(a.spent) : Infinity
        const bPrice = Number(b.spent) > 0 ? Number(b.spent) : Infinity
        return aPrice - bPrice
      })
    }

    return result
  }, [itineraries, sortMode])

  const visibleItineraries = sortedItineraries.slice(0, visibleCount)

  useEffect(() => {
    const term = parameters.city.trim()
    const hasSearch =
      Boolean(term) ||
      parameters.interests.length > 0 ||
      Boolean(parameters.min_price || parameters.max_price)

    if (!hasSearch) return

    const timer = window.setTimeout(() => {
      trackEvent({
        eventType: 'search',
        entityType: 'itinerary',
        value: term || 'filtros de roteiro',
        metadata: {
          scope: 'itineraries',
          resultCount: sortedItineraries.length,
          interests: parameters.interests,
          minPrice: parameters.min_price || null,
          maxPrice: parameters.max_price || null
        }
      })
    }, 700)

    return () => window.clearTimeout(timer)
  }, [
    parameters.city,
    parameters.interests,
    parameters.max_price,
    parameters.min_price,
    sortedItineraries.length
  ])

  useEffect(() => {
    setVisibleCount(PAGE_SIZE)
  }, [parameters, sortMode])

  const handleField: ChangeEventHandler<HTMLInputElement> = useCallback(
    (event) => {
      const { name, value } = event.target
      setParams((current) => ({ ...current, [name]: value }))
    },
    []
  )

  const handleInterests: ChangeEventHandler<HTMLInputElement> = useCallback(
    (event) => {
      const { value, checked } = event.target
      setParams((current) => ({
        ...current,
        interests: checked
          ? Array.from(new Set([...current.interests, value]))
          : current.interests.filter((interest) => interest !== value)
      }))
    },
    []
  )

  const clearFilters = () => {
    setParams({ city: '', interests: [], min_price: '', max_price: '' })
  }

  const hasFilters =
    Boolean(params.city || params.min_price || params.max_price) ||
    params.interests.length > 0

  return (
    <S.Container>
      <S.Hero>
        <S.HeroInner>
          <S.Eyebrow>Roteiros da comunidade</S.Eyebrow>
          <S.HeroTitle>
            Encontre <strong>roteiros para se inspirar</strong> e planejar sua
            próxima viagem
          </S.HeroTitle>
          <S.HeroSubtitle>
            Explore experiências da comunidade e conteúdos selecionados pela
            RedSterna, compare destinos e encontre um roteiro que combine com o
            seu jeito de viajar.
          </S.HeroSubtitle>

          <S.SearchBar>
            <FaSearch />
            <S.SearchInput
              type="text"
              placeholder="Buscar por cidade ou nome do roteiro..."
              name="city"
              value={params.city}
              onChange={handleField}
              aria-label="Buscar roteiros por cidade ou nome"
            />
          </S.SearchBar>
        </S.HeroInner>
      </S.Hero>

      <S.Content>
        {isMobileFiltersOpen && (
          <S.MobileFilterOverlay
            role="presentation"
            onMouseDown={() => setIsMobileFiltersOpen(false)}
          />
        )}

        <S.Filters
          $mobileOpen={isMobileFiltersOpen}
          onMouseDown={(event) => event.stopPropagation()}
        >
          <S.FiltersHeading>
            <div>
              <FaSlidersH />
              <span>Filtrar roteiros</span>
            </div>
            <S.MobileFilterClose
              type="button"
              aria-label="Fechar filtros"
              onClick={() => setIsMobileFiltersOpen(false)}
            >
              <FaTimes />
            </S.MobileFilterClose>
          </S.FiltersHeading>

          <S.FilterGroup>
            <S.FilterTitle>Custo total</S.FilterTitle>
            <S.FilterHint>
              Valores informados pelo autor para o roteiro completo.
            </S.FilterHint>
            <S.CostFields>
              <S.FieldLabel>
                De (R$)
                <S.CostInput
                  type="number"
                  placeholder="0"
                  step="0.01"
                  min="0"
                  name="min_price"
                  value={params.min_price}
                  onChange={handleField}
                />
              </S.FieldLabel>
              <S.FieldLabel>
                Até (R$)
                <S.CostInput
                  type="number"
                  placeholder="Sem limite"
                  step="0.01"
                  min="0"
                  name="max_price"
                  value={params.max_price}
                  onChange={handleField}
                />
              </S.FieldLabel>
            </S.CostFields>
          </S.FilterGroup>

          <S.FilterDivider />

          <S.FilterGroup>
            <S.FilterTitle>Interesses</S.FilterTitle>
            <S.FilterHint>
              Selecione os estilos de viagem que mais combinam com você.
            </S.FilterHint>
            <S.InterestList>
              {availableInterests.map((interest) => (
                <S.InterestLabel key={interest}>
                  <input
                    type="checkbox"
                    value={interest}
                    checked={params.interests.includes(interest)}
                    onChange={handleInterests}
                  />
                  <span>{formatInterest(interest)}</span>
                </S.InterestLabel>
              ))}
            </S.InterestList>
          </S.FilterGroup>

          <S.FilterActions>
            <S.ClearButton
              type="button"
              onClick={clearFilters}
              disabled={!hasFilters}
            >
              Limpar
            </S.ClearButton>
            <S.ApplyFiltersButton
              type="button"
              onClick={() => setIsMobileFiltersOpen(false)}
            >
              Aplicar filtros
            </S.ApplyFiltersButton>
          </S.FilterActions>
        </S.Filters>

        <S.Results>
          <S.MobileResultsToolbar>
            <S.MobileFilterButton
              type="button"
              onClick={() => setIsMobileFiltersOpen(true)}
            >
              <FaSlidersH />
              Filtrar
              {hasFilters && (
                <strong>
                  {params.interests.length +
                    Number(Boolean(params.min_price)) +
                    Number(Boolean(params.max_price)) +
                    Number(Boolean(params.city))}
                </strong>
              )}
            </S.MobileFilterButton>

            <S.MobileSortSelect
              value={sortMode}
              onChange={(event) => setSortMode(event.target.value as SortMode)}
              aria-label="Ordenar roteiros"
            >
              <option value="relevance">Relevância</option>
              <option value="rating">Melhor avaliados</option>
              <option value="recent">Mais recentes</option>
              <option value="price">Menor custo</option>
            </S.MobileSortSelect>
          </S.MobileResultsToolbar>

          {hasFilters && (
            <S.ActiveFilters>
              {params.min_price && (
                <button
                  type="button"
                  onClick={() =>
                    setParams((current) => ({
                      ...current,
                      min_price: ''
                    }))
                  }
                >
                  A partir de R$ {params.min_price} ×
                </button>
              )}
              {params.max_price && (
                <button
                  type="button"
                  onClick={() =>
                    setParams((current) => ({
                      ...current,
                      max_price: ''
                    }))
                  }
                >
                  Até R$ {params.max_price} ×
                </button>
              )}
              {params.interests.map((interest) => (
                <button
                  type="button"
                  key={interest}
                  onClick={() =>
                    setParams((current) => ({
                      ...current,
                      interests: current.interests.filter(
                        (item) => item !== interest
                      )
                    }))
                  }
                >
                  {formatInterest(interest)} ×
                </button>
              ))}
            </S.ActiveFilters>
          )}
          <S.ResultsHeader>
            <S.ResultCount>
              <strong>
                {isLoading
                  ? 'Buscando roteiros...'
                  : `${sortedItineraries.length} ${
                      sortedItineraries.length === 1 ? 'roteiro' : 'roteiros'
                    }`}
              </strong>
              <span>Conteúdo publicado pela comunidade RedSterna</span>
            </S.ResultCount>

            <S.SortSelect
              value={sortMode}
              onChange={(event) => setSortMode(event.target.value as SortMode)}
              aria-label="Ordenar roteiros"
            >
              <option value="relevance">Relevância</option>
              <option value="rating">Melhor avaliados</option>
              <option value="recent">Mais recentes</option>
              <option value="price">Menor custo</option>
            </S.SortSelect>
          </S.ResultsHeader>

          {isLoading ? (
            <S.EmptyState>Carregando roteiros...</S.EmptyState>
          ) : visibleItineraries.length > 0 ? (
            <>
              <S.Grid>
                {visibleItineraries.map((itinerary) => {
                  const rating = averageRating(itinerary)

                  return (
                    <S.Card
                      key={itinerary.id}
                      to={{
                        pathname: getItineraryPublicPath(itinerary),
                        state: { itinerary }
                      }}
                    >
                      <S.CardImage
                        $image={
                          itinerary.coverImage || '/images/default-photo.png'
                        }
                      >
                        {itinerary.inspiredFromItineraryId ? (
                          <S.CollaborativeBadge>
                            Roteiro Inspirado
                          </S.CollaborativeBadge>
                        ) : itinerary.isEditorial ? (
                          <S.CollaborativeBadge>
                            Editorial Redsterna
                          </S.CollaborativeBadge>
                        ) : itinerary.isCollaborative ? (
                          <S.CollaborativeBadge>
                            Roteiro colaborativo
                          </S.CollaborativeBadge>
                        ) : null}
                        {rating > 0 && (
                          <S.RatingBadge>
                            <FaStar />
                            {rating.toFixed(1)}
                          </S.RatingBadge>
                        )}
                      </S.CardImage>

                      <S.CardBody>
                        <S.CardTitle>{itinerary.title}</S.CardTitle>
                        <S.Author>
                          por{' '}
                          {itinerary.author
                            ? `${itinerary.author.firstname} ${
                                itinerary.author.lastname || ''
                              }`.trim()
                            : 'viajante RedSterna'}
                          {itinerary.isCollaborative &&
                          itinerary.collaborators?.length
                            ? ` + ${itinerary.collaborators.length} ${itinerary.collaborators.length === 1 ? 'colaborador' : 'colaboradores'}`
                            : ''}
                        </S.Author>

                        <S.MetaGrid>
                          <S.MetaItem>
                            <FaCalendarAlt />
                            <span>
                              {itinerary.days || 0}{' '}
                              {itinerary.days === 1 ? 'dia' : 'dias'}
                            </span>
                          </S.MetaItem>
                          <S.MetaItem>
                            <FaWallet />
                            <span>{formatCurrency(itinerary.spent)}</span>
                          </S.MetaItem>
                        </S.MetaGrid>
                      </S.CardBody>
                    </S.Card>
                  )
                })}
              </S.Grid>

              <S.LoadMoreArea>
                <S.LoadMoreStatus>
                  Exibindo {Math.min(visibleCount, sortedItineraries.length)} de{' '}
                  {sortedItineraries.length} roteiros
                </S.LoadMoreStatus>
                {visibleCount < sortedItineraries.length && (
                  <S.LoadMoreButton
                    type="button"
                    onClick={() =>
                      setVisibleCount((current) => current + PAGE_SIZE)
                    }
                  >
                    Ver mais roteiros
                  </S.LoadMoreButton>
                )}
              </S.LoadMoreArea>
            </>
          ) : (
            <S.EmptyState>
              Nenhum roteiro encontrado com os filtros selecionados.
            </S.EmptyState>
          )}
        </S.Results>
      </S.Content>
    </S.Container>
  )
}
